/**
 * `/` — straight into work, like Linear or Slack: the last project opened on
 * this browser if you are still in it, else your first. Everyone, workbench
 * admins too, is listed only the projects they are in. Someone in none gets the
 * first-project screen. An annotator lands on their review queues.
 */

import { useEffect } from "react";
import { ErrorState } from "@/components/ui/states";
import { lastProject, openProject } from "@/lib/project-url";
import { FirstProject } from "./first-project";
import { useProjects } from "./hooks";
import { Centered, LoadingScreen } from "./project-gate";
import type { Project } from "./types";

/** The project to open: the remembered one when still listed, else your first. */
export function pickProject(projects: Project[], last: string | null): Project | null {
  return projects.find((p) => p.slug === last) ?? projects[0] ?? null;
}

export const projectToOpen = (projects: Project[], last: string | null) =>
  pickProject(projects, last)?.slug ?? null;

/** Where in a project someone starts: an annotator has only the review queues. */
export const startPath = (project: Project) => (project.role === "annotator" ? "/annotation" : "/");

export function OpenLastProject() {
  const projects = useProjects();
  const target = projects.data ? pickProject(projects.data, lastProject()) : null;

  useEffect(() => {
    if (target) openProject(target.slug, { replace: true, path: startPath(target) });
  }, [target]);

  if (projects.isError)
    return (
      <Centered>
        <ErrorState error={projects.error} what="your projects" />
      </Centered>
    );
  if (projects.isSuccess && !target) return <FirstProject />;
  return <LoadingScreen label="Opening your project" />;
}
